import type { UploaderOptions } from '@meitu/upload';
import { getConfig } from '@/configProvider';
import { fetchObsConfig } from './services';

export enum UploadEnv {
    Release = 'Release',
    Test = 'Test',
}
interface SliceConfig {
    /** 分片大小 */
    partSize?: number;
    /** 分片上传最大并发数 */
    maxConcurrent?: number;
}

interface IConfig extends SliceConfig, Partial<Pick<UploaderOptions, 'accessToken' | 'debug'>> {
    /** x-design: 海外app，xiuxiu-pro: 国内app */
    app: string;
    /** env */
    env: UploadEnv;
    /** 桶 */
    type: string;
    /** 是否需要添加埋点 */
    needStat?: boolean;
    /** 是否需要异步获取分片配置 */
    asyncSliceConfig?: boolean;

    /**
     * 是否对上传接口参数进行加签
     *
     * @default false
     */
    signature?: boolean;
}

const DEFAULT_PART_SIZE = 4 * 1024 * 1024;

const defaultConfig: IConfig = {
    app: 'xiuxiu-pro',
    env: UploadEnv.Release,
    type: '',
    partSize: DEFAULT_PART_SIZE,
};

export const setUploadConfig = (config: Partial<IConfig>) => {
    Object.assign(defaultConfig, config);
};

export const getUploadConfig = () => {
    return defaultConfig;
};

export const getDefaultObsConfig = () => {
    const { isIntl, appName } = getConfig();
    const obsConfig: {
        sig: boolean;
        app: string;
        uploadOptions: {
            // 分片大小
            partSize?: number;
            // 分片并发数量
            maxConcurrent?: number;
        };
    } = {
        sig: false, // 是否需要加签
        app: isIntl && appName === 'x-design' ? 'x-design' : 'xiuxiu-pro', // 默认非定制版的
        uploadOptions: {},
    };

    return obsConfig;
};

// 异步获取配置信息
export const getObsConfigAsync = async (params: {
    oid?: string; // 组织id
    project?: string; // 项目moduleId
}) => {
    const obsConfig = getDefaultObsConfig();
    try {
        const resObsCfg = await fetchObsConfig(params);
        if (resObsCfg?.app) {
            obsConfig.app = resObsCfg?.app;
            obsConfig.sig = !!resObsCfg?.sig;

            // 上传配置信息
            if (resObsCfg.config.partSize) obsConfig.uploadOptions.partSize = resObsCfg.config.partSize;
            if (resObsCfg.config.concurrency) obsConfig.uploadOptions.maxConcurrent = resObsCfg.config.concurrency;
        }
    } catch (e) {
        console.error(e);
    }
    return obsConfig;
};

// 分片配置获取, 只获取一次
let sliceConfig: SliceConfig | undefined;
let sliceConfigPromise: Promise<SliceConfig> | null = null;

export const getSliceConfigAsync = async (): Promise<SliceConfig> => {
    // 已经有配置，直接返回
    if (sliceConfig) return sliceConfig;

    // 有进行中的 Promise，直接返回
    if (sliceConfigPromise) return sliceConfigPromise;

    // 创建新的 Promise 并缓存
    sliceConfigPromise = (async () => {
        const { module_id } = getConfig();
        const config: SliceConfig = {
            partSize: DEFAULT_PART_SIZE,
        };
        try {
            const resObsCfg = await fetchObsConfig({ project: module_id });

            if (resObsCfg.config.partSize) config.partSize = resObsCfg.config.partSize;
            if (resObsCfg.config.concurrency) config.maxConcurrent = resObsCfg.config.concurrency;
        } catch (e) {
            console.error(e);
        }

        sliceConfig = config;
        sliceConfigPromise = null;
        return config;
    })();

    return sliceConfigPromise;
};
